import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MapPin, X } from 'lucide-react';
import { loadGoogleMaps } from '../utils/loadGoogleMaps';
import { inp } from './FormKit';
import { useToast } from './ToastProvider';

const DEFAULT_CENTER = { lat: 19.0760, lng: 72.8777 }; // Mumbai fallback when no location is known yet

function findComponent(components, type) {
  return components.find(c => c.types.includes(type))?.long_name || '';
}

export default function MapLocationPicker({ initialLat, initialLng, onConfirm, onClose }) {
  const { showToast } = useToast();
  const mapDivRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const geocoderRef = useRef(null);

  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [position, setPosition] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [searching, setSearching] = useState(false);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    let cancelled = false;

    loadGoogleMaps().then(google => {
      if (cancelled) return;

      const hasSaved = initialLat && initialLng && !(Number(initialLat) === 0 && Number(initialLng) === 0);
      const start = hasSaved
        ? { lat: Number(initialLat), lng: Number(initialLng) }
        : DEFAULT_CENTER;

      const map = new google.maps.Map(mapDivRef.current, {
        center: start,
        zoom: hasSaved ? 15 : 5,
        streetViewControl: false,
        mapTypeControl: false,
      });
      const marker = new google.maps.Marker({ position: start, map, draggable: true });

      mapRef.current = map;
      markerRef.current = marker;
      geocoderRef.current = new google.maps.Geocoder();
      setPosition(start);
      setReady(true);

      marker.addListener('dragend', () => {
        const p = marker.getPosition();
        setPosition({ lat: p.lat(), lng: p.lng() });
      });
      map.addListener('click', e => {
        marker.setPosition(e.latLng);
        setPosition({ lat: e.latLng.lat(), lng: e.latLng.lng() });
      });

      // Only fall back to the browser's location when we don't already have a saved spot —
      // never overwrite a gym's real coordinates with wherever the owner happens to be.
      if (!hasSaved && navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          pos => {
            if (cancelled) return;
            const here = { lat: pos.coords.latitude, lng: pos.coords.longitude };
            map.setCenter(here);
            map.setZoom(15);
            marker.setPosition(here);
            setPosition(here);
          },
          () => {}, // denied or unavailable — silently keep the default center
          { timeout: 5000 }
        );
      }
    }).catch(() => {
      if (!cancelled) setLoadError(true);
    });

    return () => { cancelled = true; };
  }, [initialLat, initialLng]);

  async function handleSearch(e) {
    e.preventDefault();
    if (!searchText.trim() || !geocoderRef.current) return;
    setSearching(true);
    try {
      const { results } = await geocoderRef.current.geocode({ address: searchText.trim() });
      const first = results?.[0];
      if (!first) {
        showToast('Address not found', 'error');
        return;
      }
      const here = { lat: first.geometry.location.lat(), lng: first.geometry.location.lng() };
      mapRef.current.setCenter(here);
      mapRef.current.setZoom(15);
      markerRef.current.setPosition(here);
      setPosition(here);
    } catch {
      showToast('Search failed', 'error');
    } finally {
      setSearching(false);
    }
  }

  async function handleConfirm() {
    if (!position || !geocoderRef.current) return;
    setConfirming(true);
    try {
      const { results } = await geocoderRef.current.geocode({ location: position });
      const components = results?.[0]?.address_components || [];
      onConfirm({
        latitude: position.lat,
        longitude: position.lng,
        address: results?.[0]?.formatted_address || '',
        city: findComponent(components, 'locality') || findComponent(components, 'administrative_area_level_2'),
        state: findComponent(components, 'administrative_area_level_1'),
        zipCode: findComponent(components, 'postal_code'),
      });
    } catch {
      showToast('Coordinates set, but could not fetch the address — please fill it in manually', 'error');
      onConfirm({ latitude: position.lat, longitude: position.lng });
    } finally {
      setConfirming(false);
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-xs p-4" onClick={onClose}>
      <div className="bg-white dark:bg-[#303134] rounded-3xl w-full max-w-2xl overflow-hidden border border-[#dadce0] dark:border-[#3c4043] animate-pop-in" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#e8eaed] dark:border-[#3c4043]">
          <h3 className="font-semibold text-sm text-[#202124] dark:text-[#e8eaed] flex items-center gap-2">
            <MapPin className="w-4 h-4 text-[#1a73e8]" /> Pick Location on Map
          </h3>
          <button type="button" onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center text-[#5f6368] hover:bg-[#f1f3f4] dark:text-[#9aa0a6] dark:hover:bg-[#202124] cursor-pointer">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          {loadError ? (
            <p className="text-sm text-[#d93025]">
              Couldn't load Google Maps. Make sure REACT_APP_GOOGLE_MAPS_API_KEY is set in .env and restart the dev server.
            </p>
          ) : (
            <>
              <form onSubmit={handleSearch} className="flex gap-2">
                <input
                  value={searchText}
                  onChange={e => setSearchText(e.target.value)}
                  placeholder="Search an address…"
                  className={inp}
                />
                <button type="submit" disabled={searching || !ready}
                  className="px-4 py-2.5 rounded-full text-sm font-semibold text-white disabled:opacity-60 whitespace-nowrap bg-[#1a73e8] hover:bg-[#1557b0] cursor-pointer">
                  {searching ? '…' : 'Search'}
                </button>
              </form>

              <div ref={mapDivRef} className="w-full h-80 rounded-2xl bg-[#f1f3f4] dark:bg-[#202124]" />

              <p className="text-xs text-[#5f6368] dark:text-[#9aa0a6]">Click the map or drag the pin to set the exact spot, then confirm.</p>
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-[#e8eaed] dark:border-[#3c4043]">
          <button type="button" onClick={onClose}
            className="px-4 py-2.5 rounded-full text-sm font-semibold text-[#5f6368] dark:text-[#9aa0a6] border border-[#dadce0] dark:border-[#3c4043] hover:bg-[#f1f3f4] dark:hover:bg-[#202124] cursor-pointer">
            Cancel
          </button>
          <button type="button" onClick={handleConfirm} disabled={!ready || confirming || loadError}
            className="px-4 py-2.5 rounded-full text-sm font-semibold text-white disabled:opacity-60 bg-[#1a73e8] hover:bg-[#1557b0] cursor-pointer">
            {confirming ? 'Locating…' : 'Use This Location'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
